import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import Header from '../components/Header';
import { useAuth } from '../contexts/authContext';

const AllDisciples = () => {
  const { currentUser } = useAuth();
  const [disciplesWithMentors, setDisciplesWithMentors] = useState([]);
  const [disciplesWithoutMentors, setDisciplesWithoutMentors] = useState([]);

  useEffect(() => {
    if (currentUser) {
      fetchAllDisciples();
    }
  }, [currentUser]);

  const fetchAllDisciples = async () => {
    try {
      if (!currentUser) return;
  
      const disciplesRef = collection(db, 'disciples');
      const querySnapshot = await getDocs(disciplesRef);
      const fetchedDisciples = [];
  
      for (const docSnapshot of querySnapshot.docs) {
        const disciple = {
          id: docSnapshot.id,
          ...docSnapshot.data(),
        };
  
        // Fetch mentorDetails sub-collection for each disciple
        const mentorDetailsRef = collection(doc(db, 'disciples', disciple.id), 'mentorDetails');
        const mentorDetailsSnapshot = await getDocs(mentorDetailsRef);
  
        const mentors = mentorDetailsSnapshot.docs.map((mentorDoc) => mentorDoc.id);
        disciple.mentorCount = mentors.length;
  
        // Determine if currentUser is one of the mentors
        if (mentors.includes(currentUser.uid)) {
          disciple.isMentoredByCurrentUser = true;
          setDisciplesWithMentors((prev) => [...prev.filter((d) => d.id !== disciple.id), disciple]);
        } else if (mentors.length > 0) {
          disciple.isMentoredByCurrentUser = false;
          setDisciplesWithMentors((prev) => [...prev.filter((d) => d.id !== disciple.id), disciple]);
        } else {
          disciple.isMentoredByCurrentUser = false;
          setDisciplesWithoutMentors((prev) => [...prev.filter((d) => d.id !== disciple.id), disciple]);
        }
      }
    } catch (error) {
      console.error('Error fetching disciples:', error);
    }
  };  

  const handleAddDisciple = async (discipleId) => {
    try {
      // Reference the current user's document by their UID
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // Proceed if the user's document exists
        const discipleRef = doc(db, 'disciples', discipleId);
        const mentorDetailsRef = doc(discipleRef, 'mentorDetails', currentUser.uid);
        
        // Add the current user as a mentor in the mentorDetails sub-collection of the disciple
        await setDoc(mentorDetailsRef, {
          uid: currentUser.uid,
          addedAt: new Date(),
        });
  
        // Update the user's document to add the disciple ID
        await updateDoc(userRef, {
          discipleIds: arrayUnion(discipleId),
        });
  
        // Re-fetch disciples to update UI
        fetchAllDisciples();
      } else {
        console.error('User document not found. Cannot add disciple.');
      }
    } catch (error) {
      console.error('Error adding disciple:', error);
    }
  };

  return (
    <div>
      <Header />
      <div className="container mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6">All Disciples</h1>

        {/* Disciples with Mentors */}
        <div className="bg-white shadow-md rounded p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4">Disciples with Mentors</h2>
          {disciplesWithMentors.length > 0 ? (
            <ul className="space-y-4">
              {disciplesWithMentors.map((disciple) => (
                <li
                  key={disciple.id}
                  className="p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition duration-200"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <Link
                        to={`/disciple-menu/${disciple.id}`}
                        className="text-lg font-medium text-blue-600"
                      >
                        {disciple.name}
                      </Link>
                      <p className="text-gray-500">Number of Mentors: {disciple.mentorCount}</p>
                      {disciple.isMentoredByCurrentUser ? (
                        <p className="text-green-600 font-bold">You are mentoring</p>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No disciples with mentors found.</p>
          )}
        </div>

        {/* Disciples without Mentors */}
        <div className="bg-white shadow-md rounded p-6">
          <h2 className="text-2xl font-semibold mb-4">Disciples without Mentors</h2>
          {disciplesWithoutMentors.length > 0 ? (
            <ul className="space-y-4">
              {disciplesWithoutMentors.map((disciple) => (
                <li
                  key={disciple.id}
                  className="p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition duration-200"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <Link
                        to={`/disciple-menu/${disciple.id}`}
                        className="text-lg font-medium text-blue-600"
                      >
                        {disciple.name}
                      </Link>
                      <p className="text-gray-500">Number of Mentors: {disciple.mentorCount}</p>
                      <button
                        onClick={() => handleAddDisciple(disciple.id)}
                        className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                      >
                        Add as My Disciple
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No disciples without mentors found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllDisciples;
